import { useEffect, useMemo, useState } from "react";
import { MapPin, Star, Compass, Train, Bed } from "@phosphor-icons/react";
import {
  CITY_NAMES,
  SHRINE_SUBOPTIONS,
  parseValue,
  combine,
} from "@/utils/locationParser";

// Chips shown for a regular city selection (Pickup + Destination).
const REGULAR_CITY_CHIPS = [
  { key: "makkah", label: "مكة المكرمة", icon: MapPin },
  { key: "jeddah", label: "جدة", icon: MapPin },
  { key: "taif", label: "الطائف", icon: MapPin },
  { key: "madinah", label: "المدينة المنورة", icon: MapPin },
  { key: "makkah_train", label: "محطة قطار مكة المكرمة", icon: Train },
  { key: "madinah_train", label: "محطة قطار المدينة المنورة", icon: Train },
  { key: "hotel", label: "الفندق", icon: Bed },
];

// Extra chips shown only on the Destination picker.
// 2026-07 §ShrinesOneClick: `shrines` no longer lives here — it moved into
// a dedicated one-click row (see below) so users don't need two taps.
const SPECIAL_DEST_CHIPS = [
  { key: "tour_madinah", label: "جولة سياحية في المدينة المنورة", icon: Compass, gold: true },
];

// props:
//   label: string ("نقطة الانطلاق" | "الوجهة")
//   value: string — combined "<city name> <detail>"
//   onChange: (combined: string) => void
//   includeShrines: bool — true for Destination
//   pickupContext: string — pickup combined value (for smart shrine filter)
//   otherEndpointValue: string — the OTHER endpoint's combined value (used
//     by the hotel-hint chip). For a Pickup picker pass destination; for a
//     Destination picker pass pickup. Falls back to `pickupContext` when
//     omitted so the destination picker keeps working without a change.
//   testIdPrefix: string
export default function LocationPicker({
  label,
  value,
  onChange,
  includeShrines = false,
  pickupContext = "",
  otherEndpointValue = "",
  testIdPrefix = "loc",
  placeholder = "اكتب الموقع",
}) {
  const parsed = useMemo(() => parseValue(value || ""), [value]);
  const [city, setCity] = useState(parsed.city);
  const [detail, setDetail] = useState(parsed.location);

  // Reset detail when city switches to/from a self-contained option.
  useEffect(() => {
    setCity(parsed.city);
    setDetail(parsed.location);
  }, [parsed.city, parsed.location]);

  // Emit combined value whenever city or detail changes.
  const commit = (nextCity, nextDetail) => {
    let combined;
    if (nextCity === "shrines") {
      // Detail here holds the sub-option label (e.g., "مزارات مكة المكرمة").
      combined = (nextDetail || "").trim();
    } else if (nextCity === "tour_madinah") {
      combined = CITY_NAMES.tour_madinah;
    } else {
      combined = combine(nextCity, nextDetail);
    }
    setCity(nextCity);
    setDetail(nextDetail);
    onChange(combined);
  };

  const pickCity = (key) => {
    if (key === city) {
      // Toggle off — clear everything.
      commit(null, "");
      return;
    }
    // Switching to/from a self-contained option resets the detail.
    if (key === "shrines" || key === "tour_madinah" || city === "shrines" || city === "tour_madinah") {
      commit(key, "");
    } else {
      commit(key, detail);
    }
  };

  // 2026-07 §ShrinesOneClick: single-click shrine selection. Toggling the
  // same shrine chip clears it. Different shrine chip swaps in-place.
  const pickShrineDirect = (label) => {
    const alreadyActive = city === "shrines" && detail === label;
    if (alreadyActive) {
      commit(null, "");
    } else {
      commit("shrines", label);
    }
  };

  // Smart shrine filter: if pickup is Makkah, only show Makkah shrines, etc.
  const filteredShrines = useMemo(() => {
    if (!pickupContext) return SHRINE_SUBOPTIONS;
    const pu = parseValue(pickupContext);
    if (pu.city === "makkah") return SHRINE_SUBOPTIONS.filter((s) => s.key === "shrine_makkah");
    if (pu.city === "madinah") return SHRINE_SUBOPTIONS.filter((s) => s.key === "shrine_madinah");
    return SHRINE_SUBOPTIONS;
  }, [pickupContext]);

  // Short-label map for the one-click row (main chip row uses full labels).
  const SHRINE_SHORT_LABEL = {
    shrine_makkah: "مزارات مكة",
    shrine_madinah: "مزارات المدينة",
  };

  // 2026-02-27 §HotelHint: when THIS picker is set to "الفندق" and the OTHER
  // endpoint is one of the 4 main cities (Makkah / Madinah / Jeddah / Taif),
  // suggest a dashed "🛏️ فندق {city}" chip that pre-fills the detail input
  // with the main-city name so the admin can then append the specific hotel
  // name (e.g. "الطائف فندق كونراد"). Works for both Pickup and Destination
  // pickers via `otherEndpointValue`.
  //
  // 2026-02-27 §HotelHintTrainStations: a train station on the OTHER side
  // also triggers the hint, mapped to its host city — "محطة قطار مكة" →
  // "فندق مكة المكرمة", "محطة قطار المدينة" → "فندق المدينة المنورة".
  // Common admin pattern: airport/train arrival → hotel in same city.
  const CITY_MAP_FOR_HOTEL = {
    makkah: "مكة المكرمة",
    madinah: "المدينة المنورة",
    jeddah: "جدة",
    taif: "الطائف",
    makkah_train: "مكة المكرمة",
    madinah_train: "المدينة المنورة",
  };
  const hotelHintCityName = useMemo(() => {
    if (city !== "hotel") return null;
    const src = otherEndpointValue || pickupContext;
    if (!src) return null;
    const parsedOther = parseValue(src);
    if (!parsedOther?.city) return null;
    return CITY_MAP_FOR_HOTEL[parsedOther.city] || null;
  }, [city, otherEndpointValue, pickupContext]);

  const chips = includeShrines
    ? [...REGULAR_CITY_CHIPS, ...SPECIAL_DEST_CHIPS]
    : REGULAR_CITY_CHIPS;

  // Preview: what will actually be sent to the backend.
  const preview =
    city === "shrines"
      ? detail || null
      : city === "tour_madinah"
      ? CITY_NAMES.tour_madinah
      : combine(city, detail) || null;

  return (
    <div
      dir="rtl"
      data-testid={`${testIdPrefix}-picker`}
      className="rounded-xl border border-primary/25 bg-[#0F2148]/60 p-3 space-y-3"
    >
      {label && <div className="text-primary font-bold text-sm">{label}</div>}

      {/* Row 1 — City chips */}
      <div>
        <div className="flex items-center gap-1.5 mb-2 text-primary/80 text-xs font-semibold">
          <MapPin size={12} /> المدينة:
        </div>
        <div className="flex flex-wrap gap-2">
          {chips.map((c) => {
            const active = city === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => pickCity(c.key)}
                data-testid={`${testIdPrefix}-chip-${c.key}`}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap border ${
                  c.gold ? "border-[1.5px]" : ""
                } ${
                  active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-transparent border-primary/60 text-primary hover:bg-primary/15"
                }`}
              >
                <c.icon size={12} weight="fill" />
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 2026-07 §ShrinesOneClick: one-tap shrine chips (destination only). */}
      {includeShrines && filteredShrines.length > 0 && (
        <div data-testid={`${testIdPrefix}-shrines-row`}>
          <div className="flex items-center gap-1.5 mb-2 text-primary/80 text-xs font-semibold">
            <Star size={12} weight="fill" /> مزارات (ضغطة واحدة):
          </div>
          <div className="flex flex-wrap gap-2">
            {filteredShrines.map((s) => {
              const active = city === "shrines" && detail === s.label;
              const short = SHRINE_SHORT_LABEL[s.key] || s.label;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => pickShrineDirect(s.label)}
                  data-testid={`${testIdPrefix}-shrine-quick-${s.key}`}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap border-[1.5px] ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-transparent border-primary/60 text-primary hover:bg-primary/15"
                  }`}
                >
                  <Star size={12} weight="fill" />
                  {active ? "✓ " : ""}{short}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Row 2 — Detail input (adapts to city selection) */}
      {city === "shrines" ? (
        <div>
          <div className="text-primary/80 text-xs font-semibold mb-2 flex items-center gap-1.5">
            <Star size={12} weight="fill" /> الوجهة المحددة
          </div>
          <div className="px-3 py-2.5 rounded-lg bg-primary/10 border border-primary/40 text-primary text-sm font-bold text-center" data-testid={`${testIdPrefix}-shrine-confirm`}>
            {detail || "—"}
          </div>
        </div>
      ) : city === "tour_madinah" ? (
        <div>
          <div className="text-primary/80 text-xs font-semibold mb-2 flex items-center gap-1.5">
            <Compass size={12} weight="fill" /> الوجهة المحددة
          </div>
          <div className="px-3 py-2.5 rounded-lg bg-primary/10 border border-primary/40 text-primary text-sm font-bold text-center">
            {CITY_NAMES.tour_madinah}
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-primary/80 text-xs font-semibold">
            <MapPin size={12} /> الموقع *
          </div>
          {/* 2026-02-27 §HotelHint chip — shown ABOVE the free-text detail
              input so the admin sees a one-click shortcut before typing. */}
          {city === "hotel" && hotelHintCityName && (
            <div className="mb-2" data-testid={`${testIdPrefix}-hotel-hint-wrap`}>
              <button
                type="button"
                onClick={() => commit("hotel", hotelHintCityName)}
                data-testid={`${testIdPrefix}-hotel-hint-chip`}
                className={
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-bold border transition-colors " +
                  ((detail || "").trim().startsWith(hotelHintCityName)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-primary/10 text-primary border-primary/60 border-dashed hover:bg-primary/20")
                }
                title="اضغط لتعبئة المدينة، ثم أكمل اسم الفندق"
              >
                <Bed size={12} weight="fill" />
                فندق {hotelHintCityName}
              </button>
              <p className="text-[10.5px] text-white/50 mt-1">
                اضغط لملء المدينة، ثم أكمل اسم الفندق في نفس الحقل.
              </p>
            </div>
          )}
          <input
            type="text"
            value={detail}
            onChange={(e) => commit(city, e.target.value)}
            placeholder={placeholder}
            className="mz-input text-right"
            disabled={!city}
            data-testid={`${testIdPrefix}-detail`}
            autoComplete="off"
          />
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div
          data-testid={`${testIdPrefix}-preview`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-primary/50 text-primary/90 text-xs"
        >
          <MapPin size={12} weight="fill" /> {preview}
        </div>
      )}
    </div>
  );
}
